/**
 * VERA - Mock Data
 * Realistic case and evidence data following RMS organizational patterns
 *
 * In production, this data would be fetched from Azure Blob Storage
 * and a backend evidence management API.
 */
import type { Case, EvidenceItem } from "../types";

/**
 * Sample cases organized following RMS standards:
 * - Case number format: YYYY-CR-NNNN (year-type-sequence)
 * - Classified by incident type
 * - Status lifecycle: active → under-review → closed → archived
 */
export const mockCases: Case[] = [
  {
    id: "case-demo",
    caseNumber: "2025-CR-0042",
    title: "Residential Burglary - 1847 Oakwood Drive",
    description:
      "Residential burglary reported at 1847 Oakwood Drive. Homeowner returned to find rear sliding door forced open. Multiple items recovered from the scene and the suspect's vehicle following a traffic stop two blocks away. All physical evidence items 3D-scanned for digital preservation prior to lab submission.",
    status: "active",
    classification: "Burglary - Residential",
    assignedOfficer: "Det. Angela Morales",
    assignedUnit: "Property Crimes Unit",
    createdDate: "2025-01-22T08:30:00Z",
    lastUpdated: "2025-02-08T16:45:00Z",
    incidentDate: "2025-01-21T19:15:00Z",
    incidentLocation: "1847 Oakwood Drive, Springfield, IL 62704",
    evidenceCount: 3,
    tags: ["burglary", "residential", "firearm", "3d-scan", "real-files"],
  },
  {
    id: "case-001",
    caseNumber: "2024-CR-0847",
    title: "Armed Robbery - First National Bank",
    description:
      "Armed robbery at First National Bank, Main Street branch. Suspect entered at approximately 14:35 and demanded cash from three tellers. 3D crime scene scans captured interior layout, teller stations, and entry/exit points.",
    status: "active",
    classification: "Robbery - Armed",
    assignedOfficer: "Det. Sarah Mitchell",
    assignedUnit: "Major Crimes Unit",
    createdDate: "2024-11-15T09:30:00Z",
    lastUpdated: "2025-01-28T14:15:00Z",
    incidentDate: "2024-11-14T14:35:00Z",
    incidentLocation: "1200 Main St, Springfield, IL 62701",
    evidenceCount: 8,
    tags: ["robbery", "bank", "armed", "3d-scan", "3d-model", "priority"],
  },
  {
    id: "case-002",
    caseNumber: "2024-CR-1203",
    title: "Aggravated Assault - Downtown District",
    description:
      "Aggravated assault incident in downtown district parking structure, Level 3. Victim sustained injuries requiring hospitalization. Scene documented with 3D scanning technology including stairwell and parking area.",
    status: "active",
    classification: "Assault - Aggravated",
    assignedOfficer: "Det. Marcus Rivera",
    assignedUnit: "Violent Crimes Division",
    createdDate: "2024-12-03T11:00:00Z",
    lastUpdated: "2025-02-01T09:45:00Z",
    incidentDate: "2024-12-02T22:15:00Z",
    incidentLocation: "450 Commerce Blvd, Springfield, IL 62702",
    evidenceCount: 4,
    tags: ["assault", "parking-structure", "3d-scan"],
  },
  {
    id: "case-003",
    caseNumber: "2023-CR-0456",
    title: "Vehicle Pursuit - Highway 101 Collision",
    description:
      "High-speed vehicle pursuit ending in multi-vehicle collision on Highway 101 at mile marker 47. Scene reconstructed with 3D scanning including vehicle positions, road conditions, and debris field.",
    status: "closed",
    classification: "Traffic - Pursuit/Collision",
    assignedOfficer: "Sgt. James Park",
    assignedUnit: "Traffic Investigation Unit",
    createdDate: "2023-08-20T06:15:00Z",
    lastUpdated: "2024-03-10T16:30:00Z",
    incidentDate: "2023-08-19T23:47:00Z",
    incidentLocation: "Highway 101, Mile Marker 47, Springfield, IL",
    evidenceCount: 7,
    tags: ["pursuit", "collision", "vehicle", "3d-scan", "3d-model", "reconstruction"],
  },
  {
    id: "case-004",
    caseNumber: "2024-CR-0092",
    title: "Officer-Involved Shooting - Maple Street",
    description:
      "Officer-involved shooting incident at 800 block of Maple Street. Complete scene documentation including 3D scans of the location, trajectory analysis markers, and surrounding environment.",
    status: "under-review",
    classification: "Use of Force - OIS",
    assignedOfficer: "Lt. Katherine Chen",
    assignedUnit: "Internal Affairs / Force Investigation",
    createdDate: "2024-01-18T03:45:00Z",
    lastUpdated: "2025-01-15T10:00:00Z",
    incidentDate: "2024-01-17T21:30:00Z",
    incidentLocation: "812 Maple St, Springfield, IL 62704",
    evidenceCount: 9,
    tags: ["ois", "use-of-force", "3d-scan", "3d-model", "trajectory", "critical-incident"],
  },
];

/**
 * Evidence items linked to cases.
 * Organized following standard evidence management protocols:
 * - Unique item numbers per case
 * - Full chain of custody tracking
 * - Metadata for 3D scan files (vertex/face counts, capture device)
 *
 * Thumbnail URLs use placeholder images; in production these would be
 * rendered previews stored in Azure Blob Storage.
 */
export const mockEvidence: EvidenceItem[] = [
  // === Case 001: Armed Robbery ===
  {
    id: "ev-001-001",
    caseId: "case-001",
    itemNumber: "001-A",
    name: "Bank Interior - Full Scene Scan",
    description:
      "Complete 3D point cloud scan of bank interior including teller stations, lobby, and customer areas. Captured within 2 hours of incident.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-001/bank_interior_full.ply",
    thumbnailUrl: "",
    fileSizeMB: 245.8,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-11-14T17:00:00Z",
    collectionLocation: "1200 Main St - Interior",
    chainOfCustody: [
      {
        id: "coc-001",
        timestamp: "2024-11-14T17:00:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "1200 Main St",
        notes: "3D scan captured using Leica RTC360",
      },
      {
        id: "coc-002",
        timestamp: "2024-11-14T19:30:00Z",
        action: "Transferred to Digital Evidence Lab",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Digital Evidence Lab, HQ",
      },
      {
        id: "coc-003",
        timestamp: "2024-11-15T08:00:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "Point cloud processed, decimated for web viewing",
      },
    ],
    status: "in-custody",
    tags: ["interior", "full-scene", "point-cloud"],
    captureDevice: "Leica RTC360",
    vertexCount: 12450000,
    faceCount: 0,
  },
  {
    id: "ev-001-002",
    caseId: "case-001",
    itemNumber: "001-B",
    name: "Teller Station #2 - Detail Scan",
    description:
      "High-resolution detail scan of teller station #2 where primary interaction occurred. Includes counter surface and surrounding area.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-001/teller_station_2.ply",
    thumbnailUrl: "",
    fileSizeMB: 89.3,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-11-14T17:45:00Z",
    collectionLocation: "1200 Main St - Teller #2",
    chainOfCustody: [
      {
        id: "coc-004",
        timestamp: "2024-11-14T17:45:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "1200 Main St - Teller Station #2",
      },
      {
        id: "coc-005",
        timestamp: "2024-11-15T08:30:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["detail-scan", "teller", "interaction-point"],
    captureDevice: "Leica RTC360",
    vertexCount: 4850000,
    faceCount: 0,
  },
  {
    id: "ev-001-003",
    caseId: "case-001",
    itemNumber: "001-C",
    name: "Entry/Exit Points - Exterior Scan",
    description:
      "3D scan of bank exterior focusing on main entrance, side exit, and getaway route. Includes parking lot coverage.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-001/exterior_entry_exit.ply",
    thumbnailUrl: "",
    fileSizeMB: 178.2,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-11-14T18:30:00Z",
    collectionLocation: "1200 Main St - Exterior",
    chainOfCustody: [
      {
        id: "coc-006",
        timestamp: "2024-11-14T18:30:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "1200 Main St - Exterior",
      },
      {
        id: "coc-007",
        timestamp: "2024-11-15T09:00:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["exterior", "entry-exit", "parking"],
    captureDevice: "Leica RTC360",
    vertexCount: 8920000,
    faceCount: 0,
  },
  {
    id: "ev-001-004",
    caseId: "case-001",
    itemNumber: "001-D",
    name: "Surveillance Camera Overview",
    description:
      "Composite screenshot from bank surveillance system showing suspect entry timestamp.",
    category: "photograph",
    format: "jpg",
    fileUrl: "/evidence/case-001/surveillance_composite.jpg",
    thumbnailUrl: "",
    fileSizeMB: 4.2,
    collectedBy: "Det. Sarah Mitchell",
    collectedDate: "2024-11-14T16:00:00Z",
    collectionLocation: "Bank Security Office",
    chainOfCustody: [
      {
        id: "coc-008",
        timestamp: "2024-11-14T16:00:00Z",
        action: "Extracted from surveillance system",
        officer: "Det. Sarah Mitchell",
        badgeNumber: "D-1187",
        location: "Bank Security Office",
      },
    ],
    status: "in-custody",
    tags: ["surveillance", "photograph", "suspect-id"],
  },
  {
    id: "ev-001-005",
    caseId: "case-001",
    itemNumber: "001-E",
    name: "Vault Area - Restricted Scan",
    description:
      "3D scan of vault anteroom and vault door area. Access restricted to authorized investigators.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-001/vault_area.ply",
    thumbnailUrl: "",
    fileSizeMB: 56.7,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-11-14T18:00:00Z",
    collectionLocation: "1200 Main St - Vault Area",
    chainOfCustody: [
      {
        id: "coc-009",
        timestamp: "2024-11-14T18:00:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "1200 Main St - Vault",
      },
      {
        id: "coc-010",
        timestamp: "2024-11-15T09:15:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["vault", "restricted", "detail-scan"],
    captureDevice: "Leica RTC360",
    vertexCount: 3200000,
    faceCount: 0,
  },
  {
    id: "ev-001-006",
    caseId: "case-001",
    itemNumber: "001-F",
    name: "Bank Interior - Textured 3D Model",
    description:
      "Photogrammetry-derived textured 3D mesh model of the bank interior. Generated from 360-degree photo captures and aligned with LiDAR point cloud data for accurate geometry. Includes full PBR textures for walls, flooring, and furniture.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-001/bank_interior_model.glb",
    thumbnailUrl: "",
    fileSizeMB: 156.4,
    collectedBy: "Analyst Maria Santos",
    collectedDate: "2024-11-16T14:00:00Z",
    collectionLocation: "Digital Evidence Lab, HQ",
    chainOfCustody: [
      {
        id: "coc-036",
        timestamp: "2024-11-16T14:00:00Z",
        action: "3D model generated from photogrammetry + LiDAR fusion",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "Reality Capture processing, aligned to RTC360 scan coordinate system",
      },
      {
        id: "coc-037",
        timestamp: "2024-11-16T16:30:00Z",
        action: "Model verified and uploaded to evidence system",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["3d-model", "photogrammetry", "textured", "interior", "glb"],
    captureDevice: "Reality Capture (photogrammetry fusion)",
    vertexCount: 2450000,
    faceCount: 4800000,
  },
  {
    id: "ev-001-007",
    caseId: "case-001",
    itemNumber: "001-G",
    name: "Teller Counter - Detail 3D Model",
    description:
      "High-detail textured 3D model of teller counter area where primary suspect interaction occurred. Includes sub-millimeter detail of counter surface, cash drawer positions, and barrier glass.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-001/teller_counter_model.glb",
    thumbnailUrl: "",
    fileSizeMB: 78.2,
    collectedBy: "Analyst Maria Santos",
    collectedDate: "2024-11-16T15:30:00Z",
    collectionLocation: "Digital Evidence Lab, HQ",
    chainOfCustody: [
      {
        id: "coc-038",
        timestamp: "2024-11-16T15:30:00Z",
        action: "Detail model generated from close-range photogrammetry",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "Sub-millimeter detail model for evidence presentation",
      },
    ],
    status: "in-custody",
    tags: ["3d-model", "detail", "teller", "textured", "glb"],
    captureDevice: "Reality Capture (close-range photogrammetry)",
    vertexCount: 1850000,
    faceCount: 3600000,
  },
  {
    id: "ev-001-008",
    caseId: "case-001",
    itemNumber: "001-H",
    name: "Incident Report - Initial",
    description: "Initial incident report filed by responding officers.",
    category: "document",
    format: "pdf",
    fileUrl: "/evidence/case-001/incident_report_initial.pdf",
    thumbnailUrl: "",
    fileSizeMB: 1.8,
    collectedBy: "Off. Daniel Torres",
    collectedDate: "2024-11-14T15:30:00Z",
    collectionLocation: "1200 Main St",
    chainOfCustody: [
      {
        id: "coc-011a",
        timestamp: "2024-11-14T15:30:00Z",
        action: "Report filed",
        officer: "Off. Daniel Torres",
        badgeNumber: "P-3301",
        location: "On-scene",
      },
    ],
    status: "in-custody",
    tags: ["report", "initial", "document"],
  },

  // === Case 002: Aggravated Assault ===
  {
    id: "ev-002-001",
    caseId: "case-002",
    itemNumber: "002-A",
    name: "Parking Structure Level 3 - Full Scan",
    description:
      "Complete 3D scan of parking structure Level 3 including the assault location, stairwell access, and vehicle positions at time of incident.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-002/parking_L3_full.ply",
    thumbnailUrl: "",
    fileSizeMB: 312.5,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-12-03T01:30:00Z",
    collectionLocation: "450 Commerce Blvd - Parking L3",
    chainOfCustody: [
      {
        id: "coc-012",
        timestamp: "2024-12-03T01:30:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "450 Commerce Blvd - Level 3",
      },
      {
        id: "coc-013",
        timestamp: "2024-12-03T10:00:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["parking", "full-scene", "level-3"],
    captureDevice: "Leica RTC360",
    vertexCount: 15800000,
    faceCount: 0,
  },
  {
    id: "ev-002-002",
    caseId: "case-002",
    itemNumber: "002-B",
    name: "Stairwell B - Detail Scan",
    description:
      "High-resolution scan of Stairwell B connecting Level 3 to ground floor. Suspected entry/exit route for suspect.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-002/stairwell_B.ply",
    thumbnailUrl: "",
    fileSizeMB: 67.4,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-12-03T02:15:00Z",
    collectionLocation: "450 Commerce Blvd - Stairwell B",
    chainOfCustody: [
      {
        id: "coc-014",
        timestamp: "2024-12-03T02:15:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "450 Commerce Blvd - Stairwell B",
      },
      {
        id: "coc-015",
        timestamp: "2024-12-03T10:30:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["stairwell", "entry-exit", "detail-scan"],
    captureDevice: "Leica RTC360",
    vertexCount: 3450000,
    faceCount: 0,
  },
  {
    id: "ev-002-003",
    caseId: "case-002",
    itemNumber: "002-C",
    name: "Scene Overview Photographs",
    description:
      "Collection of 24 scene photographs documenting the assault location from multiple angles.",
    category: "photograph",
    format: "jpg",
    fileUrl: "/evidence/case-002/scene_photos.zip",
    thumbnailUrl: "",
    fileSizeMB: 156.0,
    collectedBy: "Off. Patricia Gonzalez",
    collectedDate: "2024-12-02T23:45:00Z",
    collectionLocation: "450 Commerce Blvd - Level 3",
    chainOfCustody: [
      {
        id: "coc-016",
        timestamp: "2024-12-02T23:45:00Z",
        action: "Photographs captured",
        officer: "Off. Patricia Gonzalez",
        badgeNumber: "P-2789",
        location: "450 Commerce Blvd - Level 3",
      },
    ],
    status: "in-custody",
    tags: ["photographs", "scene-overview", "multi-angle"],
  },
  {
    id: "ev-002-004",
    caseId: "case-002",
    itemNumber: "002-D",
    name: "Parking Structure CCTV - Full Video",
    description:
      "CCTV footage from parking structure cameras covering 21:00-23:30 timeframe.",
    category: "video",
    format: "mp4",
    fileUrl: "/evidence/case-002/cctv_footage.mp4",
    thumbnailUrl: "",
    fileSizeMB: 2048.0,
    collectedBy: "Det. Marcus Rivera",
    collectedDate: "2024-12-03T08:00:00Z",
    collectionLocation: "Building Security Office",
    chainOfCustody: [
      {
        id: "coc-017",
        timestamp: "2024-12-03T08:00:00Z",
        action: "Video extracted from CCTV system",
        officer: "Det. Marcus Rivera",
        badgeNumber: "D-1432",
        location: "Building Security Office",
      },
    ],
    status: "in-custody",
    tags: ["cctv", "video", "surveillance", "parking"],
  },

  // === Case 003: Vehicle Pursuit ===
  {
    id: "ev-003-001",
    caseId: "case-003",
    itemNumber: "003-A",
    name: "Collision Scene - Aerial Scan",
    description:
      "Aerial 3D scan of multi-vehicle collision scene covering full debris field, vehicle final positions, and road surface markings.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-003/collision_aerial.ply",
    thumbnailUrl: "",
    fileSizeMB: 428.9,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2023-08-20T02:00:00Z",
    collectionLocation: "Highway 101, MM 47",
    chainOfCustody: [
      {
        id: "coc-018",
        timestamp: "2023-08-20T02:00:00Z",
        action: "Aerial scan captured via drone",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Highway 101, MM 47",
        notes: "DJI Matrice 300 RTK with L1 LiDAR",
      },
      {
        id: "coc-019",
        timestamp: "2023-08-20T10:00:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["aerial", "collision", "debris-field", "drone"],
    captureDevice: "DJI Matrice 300 RTK / L1 LiDAR",
    vertexCount: 22100000,
    faceCount: 0,
  },
  {
    id: "ev-003-002",
    caseId: "case-003",
    itemNumber: "003-B",
    name: "Suspect Vehicle - Detail Scan",
    description:
      "High-resolution 3D scan of suspect vehicle (2019 Dodge Charger, black). All sides documented with emphasis on front-end collision damage.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-003/suspect_vehicle.ply",
    thumbnailUrl: "",
    fileSizeMB: 134.6,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2023-08-20T04:30:00Z",
    collectionLocation: "Highway 101, MM 47 - Vehicle Position 1",
    chainOfCustody: [
      {
        id: "coc-020",
        timestamp: "2023-08-20T04:30:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Highway 101 - Vehicle Position 1",
      },
      {
        id: "coc-021",
        timestamp: "2023-08-20T11:00:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["vehicle", "suspect", "collision-damage", "detail-scan"],
    captureDevice: "Leica RTC360",
    vertexCount: 6780000,
    faceCount: 0,
  },
  {
    id: "ev-003-003",
    caseId: "case-003",
    itemNumber: "003-C",
    name: "Victim Vehicle - Detail Scan",
    description:
      "3D scan of victim vehicle (2022 Toyota Camry, silver). Rear and passenger-side impact damage documented.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-003/victim_vehicle.ply",
    thumbnailUrl: "",
    fileSizeMB: 118.2,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2023-08-20T05:00:00Z",
    collectionLocation: "Highway 101, MM 47 - Vehicle Position 3",
    chainOfCustody: [
      {
        id: "coc-022",
        timestamp: "2023-08-20T05:00:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "Highway 101 - Vehicle Position 3",
      },
      {
        id: "coc-023",
        timestamp: "2023-08-20T11:30:00Z",
        action: "Processed and uploaded",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["vehicle", "victim", "impact-damage", "detail-scan"],
    captureDevice: "Leica RTC360",
    vertexCount: 5920000,
    faceCount: 0,
  },
  {
    id: "ev-003-004",
    caseId: "case-003",
    itemNumber: "003-D",
    name: "Road Surface & Tire Marks - Ground Scan",
    description:
      "Ground-level 3D scan documenting tire marks, skid patterns, and road surface conditions at the collision site.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-003/road_surface.ply",
    thumbnailUrl: "",
    fileSizeMB: 95.3,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2023-08-20T03:15:00Z",
    collectionLocation: "Highway 101, MM 46.5-47.2",
    chainOfCustody: [
      {
        id: "coc-024",
        timestamp: "2023-08-20T03:15:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Highway 101 - Road surface",
      },
    ],
    status: "in-custody",
    tags: ["road", "tire-marks", "skid-pattern", "ground-scan"],
    captureDevice: "Leica RTC360",
    vertexCount: 4560000,
    faceCount: 0,
  },
  {
    id: "ev-003-005",
    caseId: "case-003",
    itemNumber: "003-E",
    name: "Collision Scene - Textured 3D Reconstruction",
    description:
      "Full textured 3D mesh reconstruction of the collision scene generated from drone photogrammetry and ground-level photography. Includes road surface textures, vehicle paint details, and debris field.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-003/collision_reconstruction.glb",
    thumbnailUrl: "",
    fileSizeMB: 289.5,
    collectedBy: "Analyst Maria Santos",
    collectedDate: "2023-08-21T14:00:00Z",
    collectionLocation: "Digital Evidence Lab, HQ",
    chainOfCustody: [
      {
        id: "coc-039",
        timestamp: "2023-08-21T14:00:00Z",
        action: "3D reconstruction generated from drone + ground photogrammetry",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "Agisoft Metashape processing, 847 source images",
      },
    ],
    status: "in-custody",
    tags: ["3d-model", "reconstruction", "textured", "collision-scene", "glb"],
    captureDevice: "Agisoft Metashape (photogrammetry)",
    vertexCount: 8400000,
    faceCount: 16200000,
  },
  {
    id: "ev-003-006",
    caseId: "case-003",
    itemNumber: "003-F",
    name: "Suspect Vehicle - Textured 3D Model",
    description:
      "Photogrammetric 3D model of the suspect's 2019 Dodge Charger with full PBR textures. Shows collision damage detail, paint transfer evidence, and deformation patterns suitable for court presentation.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-003/suspect_vehicle_model.glb",
    thumbnailUrl: "",
    fileSizeMB: 112.8,
    collectedBy: "Analyst Maria Santos",
    collectedDate: "2023-08-21T16:00:00Z",
    collectionLocation: "Digital Evidence Lab, HQ",
    chainOfCustody: [
      {
        id: "coc-040",
        timestamp: "2023-08-21T16:00:00Z",
        action: "Vehicle 3D model generated from photogrammetry",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "360-degree coverage, 312 source images at impound yard",
      },
    ],
    status: "in-custody",
    tags: ["3d-model", "vehicle", "suspect", "textured", "collision-damage", "glb"],
    captureDevice: "Agisoft Metashape (vehicle photogrammetry)",
    vertexCount: 3200000,
    faceCount: 6100000,
  },
  {
    id: "ev-003-007",
    caseId: "case-003",
    itemNumber: "003-G",
    name: "Accident Reconstruction Report",
    description:
      "Final accident reconstruction report with trajectory analysis, speed estimates, and point-of-impact determination.",
    category: "document",
    format: "pdf",
    fileUrl: "/evidence/case-003/reconstruction_report.pdf",
    thumbnailUrl: "",
    fileSizeMB: 12.4,
    collectedBy: "Sgt. James Park",
    collectedDate: "2023-10-15T14:00:00Z",
    collectionLocation: "Traffic Investigation Unit",
    chainOfCustody: [
      {
        id: "coc-025",
        timestamp: "2023-10-15T14:00:00Z",
        action: "Report completed",
        officer: "Sgt. James Park",
        badgeNumber: "S-1156",
        location: "Traffic Investigation Unit",
      },
    ],
    status: "in-custody",
    tags: ["report", "reconstruction", "final"],
  },

  // === Case 004: Officer-Involved Shooting ===
  {
    id: "ev-004-001",
    caseId: "case-004",
    itemNumber: "004-A",
    name: "Maple Street - Full Scene Scan",
    description:
      "Complete 3D scan of the 800 block of Maple Street covering the entire incident area. Includes sidewalks, street, and building facades.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/maple_st_full.ply",
    thumbnailUrl: "",
    fileSizeMB: 387.1,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-01-18T01:00:00Z",
    collectionLocation: "812 Maple St - Full block",
    chainOfCustody: [
      {
        id: "coc-026",
        timestamp: "2024-01-18T01:00:00Z",
        action: "Collected - Critical Incident Protocol",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "800 block Maple St",
        notes: "Critical incident documentation protocol followed",
      },
      {
        id: "coc-027",
        timestamp: "2024-01-18T08:00:00Z",
        action: "Processed under chain-of-custody seal",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["full-scene", "critical-incident", "ois", "street"],
    captureDevice: "Leica RTC360",
    vertexCount: 19500000,
    faceCount: 0,
  },
  {
    id: "ev-004-002",
    caseId: "case-004",
    itemNumber: "004-B",
    name: "Trajectory Analysis Markers - Detail Scan",
    description:
      "High-resolution scan with trajectory analysis markers placed by forensics team. Shows bullet trajectory paths and impact points.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/trajectory_markers.ply",
    thumbnailUrl: "",
    fileSizeMB: 142.8,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-01-18T04:00:00Z",
    collectionLocation: "812 Maple St - Primary scene",
    chainOfCustody: [
      {
        id: "coc-028",
        timestamp: "2024-01-18T04:00:00Z",
        action: "Collected after forensic marker placement",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "812 Maple St",
      },
      {
        id: "coc-029",
        timestamp: "2024-01-18T09:00:00Z",
        action: "Processed under chain-of-custody seal",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
      },
    ],
    status: "in-custody",
    tags: ["trajectory", "forensic-markers", "ballistics"],
    captureDevice: "Leica RTC360",
    vertexCount: 7200000,
    faceCount: 0,
  },
  {
    id: "ev-004-003",
    caseId: "case-004",
    itemNumber: "004-C",
    name: "Officer Position - Detail Scan",
    description:
      "3D scan of officer's reported position at time of shooting. Includes cover position and line-of-sight analysis zone.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/officer_position.ply",
    thumbnailUrl: "",
    fileSizeMB: 78.5,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-01-18T02:30:00Z",
    collectionLocation: "812 Maple St - East side",
    chainOfCustody: [
      {
        id: "coc-030",
        timestamp: "2024-01-18T02:30:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "812 Maple St - East side",
      },
    ],
    status: "in-custody",
    tags: ["officer-position", "cover", "line-of-sight"],
    captureDevice: "Leica RTC360",
    vertexCount: 4100000,
    faceCount: 0,
  },
  {
    id: "ev-004-004",
    caseId: "case-004",
    itemNumber: "004-D",
    name: "Subject Position - Detail Scan",
    description:
      "3D scan of subject's last known position. Includes surrounding environment and distance measurements to officer position.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/subject_position.ply",
    thumbnailUrl: "",
    fileSizeMB: 82.1,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-01-18T03:00:00Z",
    collectionLocation: "812 Maple St - West side",
    chainOfCustody: [
      {
        id: "coc-031",
        timestamp: "2024-01-18T03:00:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "812 Maple St - West side",
      },
    ],
    status: "in-custody",
    tags: ["subject-position", "distance-analysis"],
    captureDevice: "Leica RTC360",
    vertexCount: 4350000,
    faceCount: 0,
  },
  {
    id: "ev-004-005",
    caseId: "case-004",
    itemNumber: "004-E",
    name: "Weapon Recovery Location - Detail Scan",
    description:
      "Detailed 3D scan of the area where the subject's weapon was recovered. Includes evidence markers and measurement references.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/weapon_recovery.ply",
    thumbnailUrl: "",
    fileSizeMB: 45.6,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2024-01-18T03:30:00Z",
    collectionLocation: "812 Maple St - Sidewalk west",
    chainOfCustody: [
      {
        id: "coc-032",
        timestamp: "2024-01-18T03:30:00Z",
        action: "Collected",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "812 Maple St - Sidewalk",
      },
    ],
    status: "in-custody",
    tags: ["weapon", "recovery-location", "evidence-markers"],
    captureDevice: "Leica RTC360",
    vertexCount: 2800000,
    faceCount: 0,
  },
  {
    id: "ev-004-006",
    caseId: "case-004",
    itemNumber: "004-F",
    name: "Body-Worn Camera Footage - Officer Chen",
    description:
      "Body-worn camera footage from Lt. Chen covering pre-incident approach through post-incident response.",
    category: "video",
    format: "mp4",
    fileUrl: "/evidence/case-004/bwc_chen.mp4",
    thumbnailUrl: "",
    fileSizeMB: 1536.0,
    collectedBy: "IA Investigator Thompson",
    collectedDate: "2024-01-18T05:00:00Z",
    collectionLocation: "Internal Affairs Office",
    chainOfCustody: [
      {
        id: "coc-033",
        timestamp: "2024-01-18T05:00:00Z",
        action: "BWC downloaded under IA protocol",
        officer: "IA Investigator Thompson",
        badgeNumber: "IA-0089",
        location: "Internal Affairs Office",
        notes: "Critical incident BWC download protocol followed",
      },
    ],
    status: "in-custody",
    tags: ["bwc", "body-camera", "officer", "critical-incident"],
  },
  {
    id: "ev-004-007",
    caseId: "case-004",
    itemNumber: "004-G",
    name: "Scene Photographs - 48 Images",
    description:
      "Complete photographic documentation of incident scene from all cardinal directions and key evidence points.",
    category: "photograph",
    format: "jpg",
    fileUrl: "/evidence/case-004/scene_photos.zip",
    thumbnailUrl: "",
    fileSizeMB: 312.0,
    collectedBy: "Crime Scene Tech. Adams",
    collectedDate: "2024-01-18T02:00:00Z",
    collectionLocation: "812 Maple St",
    chainOfCustody: [
      {
        id: "coc-034",
        timestamp: "2024-01-18T02:00:00Z",
        action: "Photographs captured",
        officer: "Crime Scene Tech. Adams",
        badgeNumber: "T-3877",
        location: "812 Maple St",
      },
    ],
    status: "in-custody",
    tags: ["photographs", "scene", "documentation", "all-angles"],
  },
  {
    id: "ev-004-008",
    caseId: "case-004",
    itemNumber: "004-H",
    name: "Maple Street Scene - Textured 3D Reconstruction",
    description:
      "Complete textured 3D mesh model of the 800 block of Maple Street reconstructed from LiDAR and photogrammetry data. Court-ready model with accurate scale, building facades, street features, and evidence marker positions embedded as annotations.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-004/maple_st_reconstruction.glb",
    thumbnailUrl: "",
    fileSizeMB: 342.7,
    collectedBy: "Analyst Maria Santos",
    collectedDate: "2024-01-20T10:00:00Z",
    collectionLocation: "Digital Evidence Lab, HQ",
    chainOfCustody: [
      {
        id: "coc-041",
        timestamp: "2024-01-20T10:00:00Z",
        action: "3D reconstruction generated - Critical Incident Protocol",
        officer: "Analyst Maria Santos",
        badgeNumber: "A-2205",
        location: "Digital Evidence Lab, HQ",
        notes: "LiDAR + photogrammetry fusion. 1,247 source images. Critical incident chain maintained.",
      },
      {
        id: "coc-042",
        timestamp: "2024-01-20T14:00:00Z",
        action: "Model verified by lead investigator",
        officer: "Lt. Katherine Chen",
        badgeNumber: "L-0891",
        location: "Digital Evidence Lab, HQ",
        notes: "Scale accuracy verified against physical measurements",
      },
    ],
    status: "in-custody",
    tags: ["3d-model", "reconstruction", "textured", "court-ready", "critical-incident", "glb"],
    captureDevice: "Reality Capture (LiDAR + photogrammetry fusion)",
    vertexCount: 12800000,
    faceCount: 24500000,
  },
  {
    id: "ev-004-009",
    caseId: "case-004",
    itemNumber: "004-I",
    name: "Neighboring Building - Environmental Scan",
    description:
      "3D scan of neighboring building facade and alley providing environmental context for line-of-sight analysis.",
    category: "3d-scan",
    format: "ply",
    fileUrl: "/evidence/case-004/neighboring_building.ply",
    thumbnailUrl: "",
    fileSizeMB: 198.3,
    collectedBy: "Tech. Lisa Wang",
    collectedDate: "2024-01-18T05:30:00Z",
    collectionLocation: "814-820 Maple St",
    chainOfCustody: [
      {
        id: "coc-035",
        timestamp: "2024-01-18T05:30:00Z",
        action: "Collected",
        officer: "Tech. Lisa Wang",
        badgeNumber: "T-4435",
        location: "814-820 Maple St",
      },
    ],
    status: "in-custody",
    tags: ["environment", "building", "context", "line-of-sight"],
    captureDevice: "Leica RTC360",
    vertexCount: 9800000,
    faceCount: 0,
  },

  // ═══════════════════════════════════════════════════════════
  // === Case Demo: Residential Burglary - 1847 Oakwood Drive
  // ═══════════════════════════════════════════════════════════
  //
  // GLB files location:  src/vera/public/evidence/case-demo/
  //   - evidence_1.glb  (Springfield Armory 1911 Pistol)
  //   - evidence_2.glb  (Stanley Fatmax Tape Measure)
  //   - evidence_3.glb  (Starbucks Ceramic Coffee Cup)
  //
  {
    id: "ev-demo-001",
    caseId: "case-demo",
    itemNumber: "0042-A",
    name: "Springfield Armory 1911 .45 Caliber Pistol",
    description:
      "Springfield Armory 1911 .45 ACP semi-automatic pistol recovered from the front passenger seat of the suspect's vehicle during a traffic stop at Oakwood Dr. and 5th Ave. Weapon was loaded with one round in the chamber and a full 7-round magazine. Serial number confirmed stolen out of Sangamon County (NCIC hit). Fingerprint lift attempted on grip panels and slide.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-demo/evidence_1.glb",
    thumbnailUrl: "",
    fileSizeMB: 18.4,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2025-01-21T21:45:00Z",
    collectionLocation: "Oakwood Dr. & 5th Ave. - Suspect Vehicle",
    chainOfCustody: [
      {
        id: "coc-demo-001",
        timestamp: "2025-01-21T20:30:00Z",
        action: "Recovered from suspect vehicle during traffic stop",
        officer: "Off. Brian Heller",
        badgeNumber: "P-4102",
        location: "Oakwood Dr. & 5th Ave.",
        notes: "Weapon found in plain view on front passenger seat. Made safe, magazine removed, chamber cleared.",
      },
      {
        id: "coc-demo-002",
        timestamp: "2025-01-21T21:45:00Z",
        action: "Transported to evidence room; placed in firearms locker",
        officer: "Off. Brian Heller",
        badgeNumber: "P-4102",
        location: "SPD Evidence Room, Firearms Locker 7-A",
      },
      {
        id: "coc-demo-003",
        timestamp: "2025-01-22T09:00:00Z",
        action: "Checked out for 3D scanning and latent print processing",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Digital Evidence Lab, HQ",
        notes: "3D scan captured using Artec Space Spider; latent prints lifted from slide and grip",
      },
      {
        id: "coc-demo-004",
        timestamp: "2025-01-22T14:30:00Z",
        action: "Submitted to State Crime Lab for ballistic comparison",
        officer: "Det. Angela Morales",
        badgeNumber: "D-2290",
        location: "IL State Police Forensic Lab, Springfield",
        notes: "Rush request - possible link to Sangamon Co. armed robbery (case 2024-CR-3381)",
      },
      {
        id: "coc-demo-005",
        timestamp: "2025-02-05T11:00:00Z",
        action: "Returned from State Crime Lab; re-secured in firearms locker",
        officer: "Evidence Tech. Sandra Price",
        badgeNumber: "E-1108",
        location: "SPD Evidence Room, Firearms Locker 7-A",
        notes: "Ballistic report received - positive match to Sangamon Co. case",
      },
    ],
    status: "in-lab",
    tags: ["firearm", "pistol", "stolen", "NCIC-hit", "ballistics", "latent-prints", "3d-scan"],
    captureDevice: "Artec Space Spider",
    vertexCount: 1240000,
    faceCount: 2480000,
    hasRealFile: true,
    // RMS Physical Evidence Fields
    storageLocation: "Firearms Locker 7-A, Shelf 2, SPD Evidence Room",
    condition: "Good - minor holster wear on finish; functioning condition",
    make: "Springfield Armory",
    model: "1911-A1 Mil-Spec",
    serialNumber: "NM347921",
    color: "Parkerized grey / black polymer grips",
    estimatedValue: 750,
    propertyType: "Firearm - Handgun",
    barcodeTag: "SPD-EV-2025-00421",
    labSubmitted: true,
    labNotes: "Submitted to IL State Police Forensic Lab for ballistic comparison and NIBIN entry. Latent print cards submitted separately under Lab Ref #FL-2025-0298.",
    recoveredBy: "Off. Brian Heller",
    recoveryLocation: "Front passenger seat of suspect vehicle (2018 Honda Civic, IL plate BX4 7731), Oakwood Dr. at 5th Ave.",
  },
  {
    id: "ev-demo-002",
    caseId: "case-demo",
    itemNumber: "0042-B",
    name: "Stanley FatMax 35' Tape Measure",
    description:
      "Stanley FatMax 35-foot tape measure recovered from the rear floorboard of the suspect's vehicle. Item matches description of a tape measure reported missing from the victim's garage workbench. Owner's initials 'RJM' engraved on the bottom of the housing. Fingerprint dust residue visible on the blade lock button.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-demo/evidence_2.glb",
    thumbnailUrl: "",
    fileSizeMB: 8.7,
    collectedBy: "Tech. Robert Kim",
    collectedDate: "2025-01-21T22:10:00Z",
    collectionLocation: "Oakwood Dr. & 5th Ave. - Suspect Vehicle",
    chainOfCustody: [
      {
        id: "coc-demo-006",
        timestamp: "2025-01-21T21:00:00Z",
        action: "Recovered from suspect vehicle during inventory search",
        officer: "Off. Denise Kwan",
        badgeNumber: "P-3876",
        location: "Oakwood Dr. & 5th Ave.",
        notes: "Found on rear floorboard behind driver seat. Bagged and tagged at scene.",
      },
      {
        id: "coc-demo-007",
        timestamp: "2025-01-21T22:10:00Z",
        action: "Logged into evidence room; placed in property bin",
        officer: "Evidence Tech. Sandra Price",
        badgeNumber: "E-1108",
        location: "SPD Evidence Room, Property Bin 22-D",
      },
      {
        id: "coc-demo-008",
        timestamp: "2025-01-23T10:00:00Z",
        action: "Checked out for 3D scanning and latent print processing",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Digital Evidence Lab, HQ",
        notes: "3D scan captured using Artec Space Spider; latent print lift on blade lock button",
      },
      {
        id: "coc-demo-009",
        timestamp: "2025-01-23T15:00:00Z",
        action: "Returned to evidence room after processing",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "SPD Evidence Room, Property Bin 22-D",
        notes: "Owner initials 'RJM' photographed and documented. Victim confirmed ownership.",
      },
    ],
    status: "in-custody",
    tags: ["tool", "stolen-property", "latent-prints", "victim-identified", "3d-scan"],
    captureDevice: "Artec Space Spider",
    vertexCount: 680000,
    faceCount: 1350000,
    hasRealFile: true,
    // RMS Physical Evidence Fields
    storageLocation: "Property Bin 22-D, Shelf 1, SPD Evidence Room",
    condition: "Good - normal wear consistent with use; owner initials engraved on bottom",
    make: "Stanley",
    model: "FatMax 35' (FMHT33509)",
    serialNumber: "N/A",
    color: "Yellow / black housing",
    estimatedValue: 32,
    propertyType: "Tool - Hand Tool",
    barcodeTag: "SPD-EV-2025-00422",
    labSubmitted: false,
    labNotes: "Latent prints processed in-house by Tech. Kim. One usable partial lifted from blade lock button - submitted to AFIS.",
    recoveredBy: "Off. Denise Kwan",
    recoveryLocation: "Rear floorboard (driver side) of suspect vehicle (2018 Honda Civic, IL plate BX4 7731), Oakwood Dr. at 5th Ave.",
  },
  {
    id: "ev-demo-003",
    caseId: "case-demo",
    itemNumber: "0042-C",
    name: "Starbucks Ceramic Coffee Cup",
    description:
      "White Starbucks-branded ceramic coffee mug recovered from the kitchen counter of the victim's residence at 1847 Oakwood Drive. The cup was not recognized by the homeowner and contained residual coffee. Cup was located near the point of entry (rear sliding door) and is believed to have been used by the suspect during the burglary. DNA swab collected from the rim.",
    category: "3d-scan",
    format: "glb",
    fileUrl: "/evidence/case-demo/evidence_3.glb",
    thumbnailUrl: "",
    fileSizeMB: 5.2,
    collectedBy: "Crime Scene Tech. Adams",
    collectedDate: "2025-01-21T20:45:00Z",
    collectionLocation: "1847 Oakwood Drive - Kitchen",
    chainOfCustody: [
      {
        id: "coc-demo-010",
        timestamp: "2025-01-21T20:00:00Z",
        action: "Discovered during initial scene walkthrough; photographed in place",
        officer: "Det. Angela Morales",
        badgeNumber: "D-2290",
        location: "1847 Oakwood Dr. - Kitchen counter",
        notes: "Homeowner stated cup does not belong to household. Coffee residue still warm.",
      },
      {
        id: "coc-demo-011",
        timestamp: "2025-01-21T20:45:00Z",
        action: "Collected with DNA swab of rim; bagged and sealed",
        officer: "Crime Scene Tech. Adams",
        badgeNumber: "T-3877",
        location: "1847 Oakwood Dr. - Kitchen counter",
        notes: "DNA swab taken from lip contact area on rim. Cup placed in paper evidence bag to prevent condensation.",
      },
      {
        id: "coc-demo-012",
        timestamp: "2025-01-21T23:00:00Z",
        action: "Logged into evidence room; placed in property bin",
        officer: "Evidence Tech. Sandra Price",
        badgeNumber: "E-1108",
        location: "SPD Evidence Room, Property Bin 22-E",
      },
      {
        id: "coc-demo-013",
        timestamp: "2025-01-24T09:00:00Z",
        action: "Checked out for 3D scanning",
        officer: "Tech. Robert Kim",
        badgeNumber: "T-4421",
        location: "Digital Evidence Lab, HQ",
        notes: "3D scan captured using Artec Space Spider",
      },
      {
        id: "coc-demo-014",
        timestamp: "2025-01-24T12:00:00Z",
        action: "DNA swab submitted to State Crime Lab",
        officer: "Det. Angela Morales",
        badgeNumber: "D-2290",
        location: "IL State Police Forensic Lab, Springfield",
        notes: "Rush DNA analysis requested. Lab Ref #DNA-2025-0187.",
      },
      {
        id: "coc-demo-015",
        timestamp: "2025-02-07T09:30:00Z",
        action: "DNA results received - positive match to suspect in CODIS",
        officer: "Det. Angela Morales",
        badgeNumber: "D-2290",
        location: "Property Crimes Unit, HQ",
        notes: "CODIS hit: suspect Marcus T. Dwyer (DOB 03/15/1991). Match probability >99.9999%.",
      },
    ],
    status: "in-custody",
    tags: ["container", "DNA-evidence", "suspect-linked", "CODIS-hit", "scene-recovery", "3d-scan"],
    captureDevice: "Artec Space Spider",
    vertexCount: 420000,
    faceCount: 840000,
    hasRealFile: true,
    // RMS Physical Evidence Fields
    storageLocation: "Property Bin 22-E, Shelf 1, SPD Evidence Room",
    condition: "Good - no chips or cracks; coffee residue present on interior",
    make: "Starbucks",
    model: "Classic Siren Logo Ceramic Mug, 14 oz",
    serialNumber: "N/A",
    color: "White with green Starbucks siren logo",
    estimatedValue: 15,
    propertyType: "Container - Drinking Vessel",
    barcodeTag: "SPD-EV-2025-00423",
    labSubmitted: true,
    labNotes: "DNA swab from rim submitted to IL State Police Forensic Lab. Lab Ref #DNA-2025-0187. CODIS hit returned - positive ID on suspect Marcus T. Dwyer.",
    recoveredBy: "Crime Scene Tech. Adams",
    recoveryLocation: "Kitchen counter, approximately 3 feet from rear sliding door (point of entry), 1847 Oakwood Drive",
  },
];

/** Helper: Get all evidence for a specific case */
export const getEvidenceForCase = (caseId: string): EvidenceItem[] =>
  mockEvidence.filter((e) => e.caseId === caseId);

/** Helper: Get a single evidence item by ID */
export const getEvidenceById = (id: string): EvidenceItem | undefined =>
  mockEvidence.find((e) => e.id === id);

/** Helper: Get a case by ID */
export const getCaseById = (id: string): Case | undefined =>
  mockCases.find((c) => c.id === id);

/** Helper: Get case by case number */
export const getCaseByCaseNumber = (caseNumber: string): Case | undefined =>
  mockCases.find((c) => c.caseNumber === caseNumber);
